import { useId, useLayoutEffect, useRef, type ReactNode } from 'react';
import styles from './SegmentedControl.module.css';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  /** Hide the label visually (icon only); it stays the accessible name. */
  iconOnly?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  /** Accessible name of the group. */
  label: string;
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  fullWidth?: boolean;
}

/**
 * Pick one of a few options (radio group) with a gliding thumb. Native radio
 * inputs provide arrow-key navigation.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  size = 'md',
  fullWidth = false,
}: SegmentedControlProps<T>) {
  const name = useId();
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const group = ref.current;
    const checked = group?.querySelector<HTMLElement>('input:checked')?.parentElement;
    if (!group || !checked) return;
    const place = () => {
      group.style.setProperty('--thumb-x', `${checked.offsetLeft}px`);
      group.style.setProperty('--thumb-width', `${checked.offsetWidth}px`);
    };
    place();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(place);
    observer.observe(group);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={label}
      className={[styles.group, styles[size], fullWidth && styles.fullWidth]
        .filter(Boolean)
        .join(' ')}
    >
      <span className={styles.thumb} aria-hidden="true" />
      {options.map((o) => (
        <label
          key={o.value}
          className={[styles.option, o.value === value && styles.checked].filter(Boolean).join(' ')}
          title={o.iconOnly ? o.label : undefined}
        >
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={o.value === value}
            onChange={() => onChange(o.value)}
            className={styles.input}
            {...(o.iconOnly ? { 'aria-label': o.label } : {})}
          />
          {o.icon}
          {!o.iconOnly && (
            <span className={styles.label} data-label={o.label}>
              {o.label}
            </span>
          )}
        </label>
      ))}
    </div>
  );
}
